import jsPDF from "jspdf";
import QRCode from "qrcode";

/**
 * Premium NIN Slip PDF Generator
 *
 * This utility generates PDF slips for premium NIN verification results.
 * It uses jsPDF directly to create an A4 PDF with both front and back template images.
 * The front template (premium-front.jpg) has personal data overlaid on it, and the back template
 * (premium-back.jpg) is positioned below with some spacing between them.
 * Both templates are centered on the A4 page.
 */

interface PremiumNINVerificationData {
  nin: string;
  lastName: string;
  firstName: string;
  middleName: string;
  gender: string;
  dateOfBirth: string;
  image?: string;
  issueDate?: string;
}

export const generatePremiumNINSlipPDF = async (
  data: PremiumNINVerificationData
): Promise<void> => {
  // Create A4 PDF in portrait mode
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // Constants for optimization
  const PAGE_WIDTH = 210; // A4 width in mm
  const PAGE_HEIGHT = 297; // A4 height in mm
  const PREMIUM_CARD_WIDTH = 84.912; // mm (3.343 inches)
  const PREMIUM_CARD_HEIGHT = 53.594; // mm (2.11 inches)
  const SPACE_BETWEEN = 1; // mm space between templates

  // Pre-calculate positions
  const totalHeight = PREMIUM_CARD_HEIGHT + SPACE_BETWEEN + PREMIUM_CARD_HEIGHT;
  const startY = (PAGE_HEIGHT - totalHeight) / 2;
  const startX = (PAGE_WIDTH - PREMIUM_CARD_WIDTH) / 2;
  const templateStartX = startX;
  const templateStartY = startY;

  // Helper functions
  const formatDateOfBirth = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const formatNIN = (nin: string) => {
    if (nin.length === 11) {
      return `${nin.slice(0, 4)} ${nin.slice(4, 7)} ${nin.slice(7, 11)}`;
    }
    return nin;
  };

  // Pre-format data
  const formattedDOB = formatDateOfBirth(data.dateOfBirth);
  const formattedNIN = formatNIN(data.nin);
  const fullName = `${data.firstName} ${data.middleName}`.toUpperCase();
  const qrData = {
    nin: data.nin,
    name: `${data.lastName} ${data.firstName} ${data.middleName}`.toUpperCase(),
    dob: data.dateOfBirth,
    gender: data.gender,
    issueDate: data.issueDate,
  };

  try {
    // Add instructional text above the NIN slip
    addInstructionalText(doc);

    // Load font and templates in parallel
    const [fontLoaded, frontTemplate, backTemplate] = await Promise.allSettled([
      loadOCRBFont(doc),
      loadImage("/premium-front.jpg"),
      loadImage("/premium-back.jpg"),
    ]);

    if (fontLoaded.status === "rejected") {
      console.warn("Could not load OCRB font, falling back to helvetica");
    }

    // Add front template
    if (frontTemplate.status === "rejected") {
      throw new Error("Failed to load front template");
    }
    doc.addImage(
      frontTemplate.value,
      "JPEG",
      startX,
      startY,
      PREMIUM_CARD_WIDTH,
      PREMIUM_CARD_HEIGHT
    );

    // Add user photo
    addUserPhoto(doc, data.image, templateStartX, templateStartY);

    // Add watermarks
    addWatermarks(doc, data.nin, templateStartX, templateStartY);

    // Add personal information
    addPersonalInfo(
      doc,
      data,
      formattedDOB,
      formattedNIN,
      fullName,
      templateStartX,
      templateStartY
    );

    // Add issue date
    addIssueDate(doc, data.issueDate, templateStartX, templateStartY);

    // Generate and add QR code
    await addQRCode(
      doc,
      qrData,
      templateStartX,
      templateStartY,
      PREMIUM_CARD_WIDTH
    );

    // Add back template
    if (backTemplate.status === "rejected") {
      throw new Error("Failed to load back template");
    }
    doc.addImage(
      backTemplate.value,
      "JPEG",
      startX,
      startY + PREMIUM_CARD_HEIGHT + SPACE_BETWEEN,
      PREMIUM_CARD_WIDTH,
      PREMIUM_CARD_HEIGHT
    );

    // Save PDF
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `Premium_NIN_Slip_${data.nin}_${timestamp}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error("Error generating premium NIN slip PDF:", error);
    throw error;
  }
};

// Optimized helper functions
const addInstructionalText = (doc: jsPDF): void => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);

  // Position the text above the NIN slip
  const textY = 20; // Position above the slip
  const centerX = 105; // Center of A4 page

  // Add the three lines of instructional text
  doc.text(
    "Please find below your new High Resolution NIN Slip.",
    centerX,
    textY,
    { align: "center" }
  );
  doc.text(
    "You may cut it out of the paper, fold and laminate as desired.",
    centerX,
    textY + 6,
    { align: "center" }
  );
  doc.text(
    "Please DO NOT allow others to make copies of your NIN Slip.",
    centerX,
    textY + 12,
    { align: "center" }
  );
};

const loadOCRBFont = async (doc: jsPDF): Promise<void> => {
  const fontPath = "/fonts/OCRB Regular.ttf";
  const fontData = await fetch(fontPath).then((res) => res.arrayBuffer());
  const base64Font = btoa(String.fromCharCode(...new Uint8Array(fontData)));
  doc.addFileToVFS("OCRB-Regular.ttf", base64Font);
  doc.addFont("OCRB-Regular.ttf", "OCRB", "normal");
};

const addWatermarks = (
  doc: jsPDF,
  nin: string,
  templateStartX: number,
  templateStartY: number
): void => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);

  // Use a darker color for better visibility on colored background
  doc.setTextColor(200, 200, 200);

  const watermarkPositions = [
    // right side
    { x: templateStartX + 65, y: templateStartY + 44, angle: -30 },
    { x: templateStartX + 64, y: templateStartY + 29, angle: 0 },
    // left sid8
    { x: templateStartX + 9, y: templateStartY + 44, angle: 50 },
    { x: templateStartX + 10, y: templateStartY + 52, angle: 50 },
  ];

  watermarkPositions.forEach((pos) => {
    doc.text(nin, pos.x, pos.y, { angle: pos.angle });
  });

  doc.setTextColor(0, 0, 0);
};

const addUserPhoto = (
  doc: jsPDF,
  image: string | undefined,
  templateStartX: number,
  templateStartY: number
): void => {
  const photoX = templateStartX + 2;
  const photoY = templateStartY + 12;
  const photoWidth = 20;
  const photoHeight = 23;

  if (image) {
    try {
      let imageData = image;
      if (
        !imageData.startsWith("data:") &&
        !imageData.startsWith("http") &&
        !imageData.startsWith("/")
      ) {
        imageData = `data:image/jpeg;base64,${image}`;
      }
      doc.addImage(imageData, "JPEG", photoX, photoY, photoWidth, photoHeight);
    } catch (error) {
      console.log("Could not process user image for PDF");
      drawPhotoPlaceholder(doc, photoX, photoY, photoWidth, photoHeight);
    }
  } else {
    drawPhotoPlaceholder(doc, photoX, photoY, photoWidth, photoHeight);
  }
};

const drawPhotoPlaceholder = (
  doc: jsPDF,
  photoX: number,
  photoY: number,
  photoWidth: number,
  photoHeight: number
): void => {
  doc.setFillColor(200, 200, 200);
  doc.rect(photoX, photoY, photoWidth, photoHeight, "F");
  doc.setFillColor(0, 0, 0);
  doc.text("Photo", photoX + photoWidth / 2 - 5, photoY + photoHeight / 2);
};

const addPersonalInfo = (
  doc: jsPDF,
  data: PremiumNINVerificationData,
  formattedDOB: string,
  formattedNIN: string,
  fullName: string,
  templateStartX: number,
  templateStartY: number
): void => {
  const textStartX = templateStartX + 9;
  const textStartY = templateStartY + 20;
  const lineHeight = 7;

  doc.setFont("OCRB", "normal");
  doc.setFontSize(9);

  // Surname
  doc.text(data.lastName.toUpperCase(), textStartX + 14, textStartY - 1);

  // Given Names
  doc.text(fullName, textStartX + 14, textStartY + lineHeight);

  // Date of Birth
  doc.text(formattedDOB, textStartX + 14, textStartY + lineHeight * 2);

  // Gender (M/F) - positioned to the right of Date of Birth
  const genderText = data.gender
    ? data.gender.toLowerCase() === "male"
      ? "M"
      : data.gender.toLowerCase() === "female"
      ? "F"
      : data.gender.toUpperCase()
    : "M";
  doc.text(genderText, textStartX + 37, textStartY + lineHeight * 2);

  // NIN Numbe8
  doc.setFontSize(16);
  doc.text(formattedNIN, textStartX + 9, textStartY + 2 + lineHeight * 4.2);
};

const addIssueDate = (
  doc: jsPDF,
  issueDate: string | undefined,
  templateStartX: number,
  templateStartY: number
): void => {
  if (!issueDate) return;

  const formatIssueDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const monthNames = [
        "JAN",
        "FEB",
        "MAR",
        "APR",
        "MAY",
        "JUN",
        "JUL",
        "AUG",
        "SEP",
        "OCT",
        "NOV",
        "DEC",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const formattedIssueDate = formatIssueDate(issueDate);

  // Position for issue date (adjust these coordinates based on template)
  const issueDateX = templateStartX + 62; // Right side of the card
  const issueDateY = templateStartY + 40; // Below the QR code area

  doc.setFont("OCRB", "normal");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);

  // Add the issue date
  doc.text(formattedIssueDate, issueDateX, issueDateY);
};

const addQRCode = async (
  doc: jsPDF,
  qrData: any,
  templateStartX: number,
  templateStartY: number,
  templateWidth: number
): Promise<void> => {
  const qrX = templateStartX + templateWidth - 24;
  const qrY = templateStartY + 5;
  const qrSize = 22;

  try {
    const qrCodeDataURL = await QRCode.toDataURL(JSON.stringify(qrData), {
      width: 200,
      margin: 0,
      color: { dark: "#000000", light: "#FFFFFF" },
    });
    doc.addImage(qrCodeDataURL, "PNG", qrX, qrY, qrSize, qrSize);
  } catch (error) {
    console.warn("Failed to generate QR code:", error);
    doc.setFillColor(0, 0, 0);
    doc.rect(qrX, qrY, qrSize, qrSize, "F");
    doc.setFillColor(255, 255, 255);
    doc.rect(qrX + 1, qrY + 1, qrSize - 2, qrSize - 2, "F");
  }
};

// Helper function to load image
const loadImage = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Check if it's already a data URL (base64)
    if (src.startsWith("data:")) {
      resolve(src);
      return;
    }

    // Check if it's base64 data from API (not a data URL and not a regular URL)
    if (
      !src.startsWith("data:") &&
      !src.startsWith("http") &&
      !src.startsWith("/")
    ) {
      // It's base64 data from API, add the proper data URL prefix
      const dataUrl = `data:image/jpeg;base64,${src}`;
      resolve(dataUrl);
      return;
    }

    // Otherwise, treat it as a regular URL
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = reject;
    img.src = src;
  });
};
